"use client";

import { NAKSHATRAS, RASHIS, type RashiInfo } from "./ashtakoot-milan-data";

import { useMemo, useState } from "react";
import Link from "next/link";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";
import KundaliMatchCardGenerator from "@/components/KundaliMatchCardGenerator";

interface MilanParams {
  gRashi: RashiInfo;
  gNak: (typeof NAKSHATRAS)[0];
  bRashi: RashiInfo;
  bNak: (typeof NAKSHATRAS)[0];
  gRashiIdx: number;
  gNakIdx: number;
  bRashiIdx: number;
  bNakIdx: number;
  isHindi: boolean;
}

function computeVarnaScore(g: RashiInfo, b: RashiInfo): number {
  const varnaRank = { Brahmin: 4, Kshatriya: 3, Vaishya: 2, Shudra: 1 };
  return varnaRank[g.varna] >= varnaRank[b.varna] ? 1 : 0;
}

function computeVashyaScore(g: RashiInfo, b: RashiInfo): number {
  if (g.vashya === b.vashya) return 2;
  return g.element === b.element ? 1 : 0.5;
}

function computeGanaScore(gGana: string, bGana: string): number {
  if (gGana === bGana) return 6;
  if (gGana === "Rakshasa" || bGana === "Rakshasa") return 1;
  return 4;
}

function computeBhakootScore(gRashi: RashiInfo, bRashi: RashiInfo, gIdx: number, bIdx: number) {
  const rashiDist = ((bIdx - gIdx + 12) % 12) + 1;
  const isDosha = [2, 6, 8, 12].includes(rashiDist);
  const isCancelled = gRashi.ruler === bRashi.ruler || gRashi.element === bRashi.element;
  return { points: !isDosha || isCancelled ? 7 : 0, isDosha, isCancelled };
}

function computeNadiScore(
  gNak: (typeof NAKSHATRAS)[0],
  bNak: (typeof NAKSHATRAS)[0],
  isParihar: boolean
) {
  const isSame = gNak.nadi === bNak.nadi;
  return { points: !isSame || isParihar ? 8 : 0, isDosha: isSame && !isParihar, isParihar };
}

function computeMilanMath(params: MilanParams) {
  const varnaPts = computeVarnaScore(params.gRashi, params.bRashi);
  const vashyaPts = computeVashyaScore(params.gRashi, params.bRashi);
  const taraPts = ((params.bNakIdx - params.gNakIdx + 27) % 9) % 2 === 0 ? 3 : 1.5;
  const yoniPts = params.gNak.yoni === params.bNak.yoni ? 4 : 2;
  const grahaPts =
    params.gRashi.ruler === params.bRashi.ruler
      ? 5
      : params.gRashi.element === params.bRashi.element
        ? 4
        : 3;
  const ganaPts = computeGanaScore(params.gNak.gana, params.bNak.gana);
  const bhakoot = computeBhakootScore(
    params.gRashi,
    params.bRashi,
    params.gRashiIdx,
    params.bRashiIdx
  );
  const isNadiParihar = params.gRashiIdx === params.bRashiIdx && params.gNakIdx !== params.bNakIdx;
  const nadi = computeNadiScore(params.gNak, params.bNak, isNadiParihar);

  const total =
    varnaPts + vashyaPts + taraPts + yoniPts + grahaPts + ganaPts + bhakoot.points + nadi.points;

  return {
    total,
    breakdown: [
      { name: params.isHindi ? "वर्ण कूट" : "Varna", max: 1, obtained: varnaPts },
      { name: params.isHindi ? "वश्य कूट" : "Vashya", max: 2, obtained: vashyaPts },
      { name: params.isHindi ? "तारा कूट" : "Tara", max: 3, obtained: taraPts },
      { name: params.isHindi ? "योनि कूट" : "Yoni", max: 4, obtained: yoniPts },
      { name: params.isHindi ? "ग्रह मैत्री" : "Graha Maitri", max: 5, obtained: grahaPts },
      { name: params.isHindi ? "गण कूट" : "Gana", max: 6, obtained: ganaPts },
      {
        name: params.isHindi ? "भकूट कूट" : "Bhakoot",
        max: 7,
        obtained: bhakoot.points,
        cancelled: bhakoot.isDosha && bhakoot.isCancelled,
      },
      {
        name: params.isHindi ? "नाड़ी कूट" : "Nadi",
        max: 8,
        obtained: nadi.points,
        cancelled: nadi.isParihar,
      },
    ],
    isNadiDosha: nadi.isDosha,
  };
}

export default function AshtakootMilanStudio({ isHindi = false }: { isHindi?: boolean }) {
  const [groomRashiIndex, setGroomRashiIndex] = useState(0);
  const [groomNakshatraIndex, setGroomNakshatraIndex] = useState(0);
  const [brideRashiIndex, setBrideRashiIndex] = useState(4);
  const [brideNakshatraIndex, setBrideNakshatraIndex] = useState(9);

  const groomRashi = RASHIS[groomRashiIndex];
  const brideRashi = RASHIS[brideRashiIndex];
  const groomNak = NAKSHATRAS[groomNakshatraIndex];
  const brideNak = NAKSHATRAS[brideNakshatraIndex];

  const milanResult = useMemo(
    () =>
      computeMilanMath({
        gRashi: groomRashi,
        gNak: groomNak,
        bRashi: brideRashi,
        bNak: brideNak,
        gRashiIdx: groomRashiIndex,
        gNakIdx: groomNakshatraIndex,
        bRashiIdx: brideRashiIndex,
        bNakIdx: brideNakshatraIndex,
        isHindi,
      }),
    [
      groomRashi,
      groomNak,
      brideRashi,
      brideNak,
      groomRashiIndex,
      groomNakshatraIndex,
      brideRashiIndex,
      brideNakshatraIndex,
      isHindi,
    ]
  );

  const handleConsult = () => {
    safeSessionSet(
      "ishvaram_intent_context",
      JSON.stringify({
        source: "/kundli-matching",
        groom: groomRashi.name,
        bride: brideRashi.name,
        score: milanResult.total,
      })
    );
  };

  const chatHref = buildSEOAgentHref(
    getSEOIntentConfigForSource("/kundli-matching"),
    "/kundli-matching",
    isHindi ? "hi" : "en"
  );
  const shareText = encodeURIComponent(
    `Vedic 36-Gun Milan Score: ${milanResult.total}/36 between ${groomRashi.name} & ${brideRashi.name} on Ishvaram. https://ishvaram.com/kundli-matching/`
  );
  const whatsappShareUrl = `https://api.whatsapp.com/send?text=${shareText}`;

  return (
    <div className="my-8 rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-7 shadow-xs">
      <StudioHeader isHindi={isHindi} whatsappShareUrl={whatsappShareUrl} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
        <PersonSelector
          title={isHindi ? "वर (Groom) का विवरण" : "Groom (Var) Details"}
          icon="🤵"
          isHindi={isHindi}
          rashiIdx={groomRashiIndex}
          nakIdx={groomNakshatraIndex}
          onRashiChange={setGroomRashiIndex}
          onNakChange={setGroomNakshatraIndex}
        />
        <PersonSelector
          title={isHindi ? "वधू (Bride) का विवरण" : "Bride (Kanya) Details"}
          icon="👰"
          isHindi={isHindi}
          rashiIdx={brideRashiIndex}
          nakIdx={brideNakshatraIndex}
          onRashiChange={setBrideRashiIndex}
          onNakChange={setBrideNakshatraIndex}
        />
      </div>

      <ResultCard
        isHindi={isHindi}
        milanResult={milanResult}
        chatHref={chatHref}
        onConsult={handleConsult}
      />

      <KundaliMatchCardGenerator
        groomName={isHindi ? "वर" : "Groom"}
        brideName={isHindi ? "वधू" : "Bride"}
        groomRashi={isHindi ? groomRashi.hindi : groomRashi.name}
        brideRashi={isHindi ? brideRashi.hindi : brideRashi.name}
        score={milanResult.total}
        isHindi={isHindi}
      />
    </div>
  );
}

function StudioHeader({
  isHindi,
  whatsappShareUrl,
}: {
  isHindi: boolean;
  whatsappShareUrl: string;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-border-default">
      <div>
        <span className="text-caption font-bold uppercase tracking-wider text-intent-brand">
          {isHindi ? "36 गुण मिलान सॉफ्टवेयर" : "36-Point Gun Milan Software"}
        </span>
        <h2 className="text-2xl font-bold font-serif text-text-primary mt-1">
          {isHindi
            ? "अष्टकूट कुंडली मिलान व नाड़ी दोष कैलकुलेटर"
            : "Ashtakoot Kundli Matching & Nadi Dosha Calculator"}
        </h2>
      </div>
      <a
        href={whatsappShareUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 rounded-lg border border-border-default bg-surface-raised px-3.5 py-2 text-caption font-bold text-text-primary hover:bg-surface-warm transition-colors shrink-0"
      >
        <span>📱</span>
        <span>{isHindi ? "WhatsApp पर शेयर करें" : "Share on WhatsApp"}</span>
      </a>
    </div>
  );
}

function PersonSelector({
  title,
  icon,
  isHindi,
  rashiIdx,
  nakIdx,
  onRashiChange,
  onNakChange,
}: {
  title: string;
  icon: string;
  isHindi: boolean;
  rashiIdx: number;
  nakIdx: number;
  onRashiChange: (i: number) => void;
  onNakChange: (i: number) => void;
}) {
  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-4">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border-default">
        <span className="text-xl">{icon}</span>
        <h3 className="text-body font-bold text-text-primary">{title}</h3>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-caption font-semibold text-text-secondary block mb-1">
            {isHindi ? "चंद्र राशि" : "Moon Rashi"}
          </label>
          <select
            value={rashiIdx}
            onChange={(e) => onRashiChange(Number(e.target.value))}
            className="w-full rounded-lg border border-border-default bg-surface-raised px-3 py-2 text-body-small text-text-primary font-medium focus:border-intent-brand focus:outline-none"
          >
            {RASHIS.map((r, i) => (
              <option key={r.id} value={i}>
                {isHindi ? r.hindi : r.name} — {r.ruler}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-caption font-semibold text-text-secondary block mb-1">
            {isHindi ? "जन्म नक्षत्र" : "Birth Nakshatra"}
          </label>
          <select
            value={nakIdx}
            onChange={(e) => onNakChange(Number(e.target.value))}
            className="w-full rounded-lg border border-border-default bg-surface-raised px-3 py-2 text-body-small text-text-primary font-medium focus:border-intent-brand focus:outline-none"
          >
            {NAKSHATRAS.map((n, i) => (
              <option key={n.name} value={i}>
                {n.name} ({n.gana}, {n.nadi})
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

function ResultCard({
  isHindi,
  milanResult,
  chatHref,
  onConsult,
}: {
  isHindi: boolean;
  milanResult: ReturnType<typeof computeMilanMath>;
  chatHref: string;
  onConsult: () => void;
}) {
  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border-default">
        <div>
          <span className="text-caption font-bold uppercase tracking-wider text-text-muted">
            {isHindi ? "कुल प्राप्त अंक" : "Total Score"}
          </span>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-4xl font-serif font-black text-text-primary">
              {milanResult.total}
            </span>
            <span className="text-lg font-semibold text-text-muted">
              / 36 {isHindi ? "गुण" : "Gunas"}
            </span>
          </div>
        </div>
        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-5 py-2.5 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm"
        >
          {isHindi ? "विवाह विशेषज्ञ से पूछें ✦" : "Analyze Compatibility with AI ✦"}
        </a>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {milanResult.breakdown.map((koot, i) => (
          <div key={i} className="p-3 rounded-lg border border-border-default bg-surface-raised">
            <div className="flex items-center justify-between text-caption font-bold mb-1">
              <span className="text-text-primary truncate">{koot.name}</span>
              <span className="text-intent-brand font-mono shrink-0 ml-1">
                {koot.obtained} / {koot.max}
              </span>
            </div>
            <div className="w-full bg-surface-warm rounded-full h-1.5 overflow-hidden">
              <div
                className={`bg-intent-brand h-full rounded-full transition-all duration-300 ${
                  koot.obtained / koot.max >= 1
                    ? "w-full"
                    : koot.obtained / koot.max >= 0.7
                      ? "w-3/4"
                      : koot.obtained / koot.max >= 0.4
                        ? "w-1/2"
                        : koot.obtained / koot.max > 0
                          ? "w-1/4"
                          : "w-0"
                }`}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 pt-4 border-t border-border-default flex flex-col sm:flex-row items-center justify-between gap-3 text-caption">
        <span className="text-text-secondary">
          {milanResult.isNadiDosha
            ? isHindi
              ? "⚠️ नाड़ी दोष उपस्थित है — पूर्ण कुंडली में परिहार की जांच कराएं।"
              : "⚠️ Nadi Dosha detected — verify specific cancellation yogas."
            : isHindi
              ? "✓ नाड़ी अनुकूल है या शास्त्रानुसार परिहार लागू है।"
              : "✓ Nadi is compatible or cancelled per classical BPHS rules."}
        </span>
        <Link
          href={chatHref}
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1 shrink-0"
        >
          {isHindi ? "नाड़ी दोष निवारण परामर्श →" : "Get Nadi Dosha Advice →"}
        </Link>
      </div>
    </div>
  );
}
